import React, { useState, useEffect } from 'react';
import { 
  Button, DatePicker, Space, Divider,
  Typography, Popover
} from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from '../../../dayjs';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const OTRDateRangePicker = ({ 
  value, 
  onChange, 
  style = {},
  buttonStyle = {},
  displayFormat = 'DD MMM YYYY',
  placeholder = 'Select Date Range'
}) => {
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [dateRange, _setDateRange] = useState(() => {
    if (value && Array.isArray(value) && value.length === 2) {
      return [value[0], value[1].endOf('day')];
    }
    const today = dayjs().endOf('day');
    const thirtyDaysAgo = today.clone().subtract(30, 'day');
    return [thirtyDaysAgo, today];
  });
  const [selectedDatePreset, setSelectedDatePreset] = useState('last30'); // Default to last30

  const setDateRange = (range) => {
    if (Array.isArray(range) && range.length === 2 && range[1]) {
      _setDateRange([range[0], range[1].endOf('day')]);
    } else {
      _setDateRange(range);
    }
  };

  // Update parent when dates change internally
  useEffect(() => {
    if (onChange && dateRange && dateRange[0] && dateRange[1] && 
        (!value || !value[0]?.isSame(dateRange[0]) || !value[1]?.isSame(dateRange[1]))) {
      onChange(dateRange);
    }
  }, [dateRange, onChange, value]);

  // Initialize with default "Last 30 days" on component mount
  useEffect(() => {
    // Only run once on mount, if no value was provided
    if (!value && onChange && !dateRange[0] && !dateRange[1]) {
      const today = dayjs().endOf('day');
      const thirtyDaysAgo = today.clone().subtract(30, 'day');
      onChange([thirtyDaysAgo, today]);
    }
  }, []); // Empty dependency array since we only want this to run once on mount

  const handleDateRangeChange = (dates) => {
    if (dates && Array.isArray(dates) && dates.length === 2) {
      const startDate = dates[0];
      const endDate = dates[1].endOf('day');
      if (!startDate || !endDate) {
        console.error('Invalid dates:', dates);
        return;
      }
      setDateRange([startDate, endDate]);
      setSelectedDatePreset('custom');
    }
  };

  // --- Custom Presets for OSRReport ---
  const renderLastNMonthsOption = (months) => {
    const today = dayjs().endOf('month');
    const startDate = today.clone().subtract(months - 1, 'month').startOf('month');
    const label = `Last ${months} ${months === 1 ? 'month' : 'months'}`;
    return (
      <div 
        className="date-preset-option" 
        onClick={() => {
          setDateRange([startDate, today]);
          setSelectedDatePreset(`last${months}months`);
        }}
        style={{ 
          padding: '12px 16px', 
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: selectedDatePreset === `last${months}months` ? '#f0f7ff' : 'transparent',
          borderRadius: '4px'
        }}
      >
        <span>{label}</span>
        <span style={{ color: '#888' }}>{startDate.format('D MMM')} - {today.format('D MMM')}</span>
      </div>
    );
  };

  const handleResetToDefault = () => {
    // Default to last 3 months
    const today = dayjs().endOf('month');
    const startDate = today.clone().subtract(2, 'month').startOf('month');
    setDateRange([startDate, today]);
    setSelectedDatePreset('last3months');
  };

  const content = (
    <div style={{ 
      width: "330px",
      backgroundColor: 'white',
      padding: '16px'
    }}>
      {/* Date range picker at top */}
      <div style={{ marginBottom: '20px' }}>
        <Text strong style={{ display: 'block', marginBottom: '12px' }}>Select Custom Range</Text>
        <RangePicker 
          style={{ width: '100%' }}
          value={dateRange[0] && dateRange[1] ? [dateRange[0], dateRange[1]] : null}
          onChange={(dates) => {
            if (dates && dates.length === 2) {
              handleDateRangeChange(dates);
            }
          }}
          allowClear={false}
        />
      </div>
      <Divider style={{ margin: '16px 0' }} />
      {/* Preset options below */}
      <div>
        <Text strong style={{ display: 'block', marginBottom: '12px' }}>Preset Ranges</Text>
        {renderLastNMonthsOption(1)}
        {renderLastNMonthsOption(3)}
        {renderLastNMonthsOption(6)}
        {renderLastNMonthsOption(12)}
        <Divider style={{ margin: '12px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
          <Button
            onClick={() => handleResetToDefault()}
            style={{ color: '#1890ff' }}
            type="link"
          >
            Reset to default
          </Button>
          <Button 
            type="primary"
            onClick={() => setCalendarVisible(false)}
          >
            Apply
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="otr-date-range-picker" style={{ ...style }}>
      <Popover 
        content={content}
        trigger="click"
        open={calendarVisible}
        onOpenChange={setCalendarVisible}
        placement="bottom"
        overlayStyle={{ padding: 0 }}
      >
        <Button 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            ...buttonStyle
          }}
        >
          <div>
            {dateRange && dateRange[0] && dateRange[1] 
              ? `${dateRange[0].format(displayFormat)} - ${dateRange[1].format(displayFormat)}` 
              : placeholder}
          </div>
          <CalendarOutlined />
        </Button>
      </Popover>
    </div>
  );
};

export default OTRDateRangePicker; 